<!--
 * @Author: maogangfeng
 * @Date: 2020-03-09 11:00:25
 * @Desc: 预警类型数量统计
 -->
<template>
  <div>
    <panel-title title="预警类型数量统计" width="196" />
    <div class="chart">
      <div ref="typeChart"></div>
    </div>
  </div>
</template>
<script>
import G2 from '@antv/g2'
import PanelTitle from './PanelTitle.vue'
import { COLORPICKER } from './constants'
export default {
  name: 'WarningTypeStatistics',
  components: { PanelTitle },
  props: {
    data: {
      type: Array,
      required: true
    },

    // 屏幕尺寸类型
    sizeMode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      chart: null,
      chartView: null,
      sliceNumber: 0.01, // 饼图分割空白

      // 图表尺寸
      chartSize: {
        sm: {
          height: 250,
          width: 250
        },
        md: {
          height: 310,
          width: 310
        },
        lg: {
          height: 350,
          width: 352
        }
      },
      eventCount: 0, // 事件总数
      legendItemEvent: [] // 图例子元素的事件绑定对象集合
    }
  },
  watch: {
    // 图表数据源
    data(newValue) {
      this.renderChart()
    },
    sizeMode(data) {
      this.changeChartSize()
    }
  },

  mounted() {
    this.registerShape(this.sliceNumber)
    this.data.length && this.renderChart()
  },
  beforeDestroy() {
    this.legendItemEvent.forEach(i => {
      i.removeEventListener('click', this.handleClickLengendItem)
    })
    this.legendItemEvent = null
  },
  methods: {
    /**
     * @Author: maogangfeng
     * @Date: 2020-03-12 14:58:38
     * @Desc: 自定义 other 的图形，增加两条线
     */
    registerShape(sliceNumber) {
      G2.Shape.registerShape('interval', 'slice-shape', {
        draw(cfg, container) {
          const points = cfg.points
          let path = []
          path.push(['M', points[0].x, points[0].y])
          path.push(['L', points[1].x, points[1].y - sliceNumber])
          path.push(['L', points[2].x, points[2].y - sliceNumber])
          path.push(['L', points[3].x, points[3].y])
          path.push('Z')
          path = this.parsePath(path)
          return container.addShape('path', {
            attrs: {
              fill: cfg.color,
              path
            }
          })
        }
      })
    },
    /**
     * @Author: maogangfeng
     * @Date: 2020-03-10 11:12:24
     * @Desc: 渲染饼图
     */
    renderChart() {
      const data = this.data
      const { width, height } = this.chartSize[this.sizeMode]
      let chart = this.chart
      if (!chart) {
        chart = new G2.Chart({
          container: this.$refs.typeChart,
          height,
          width,
          padding: 'auto'
        })
        this.chart = chart
      } else {
        this.chart.clear()
      }
      chart.source(data)
      // 用于绘制图标的辅助元素
      const total = data.length
        ? data.reduce((accumulator, currentValue) => accumulator + currentValue.number, 0)
        : 0
      this.eventCount = total
      this.setChartGuide(chart, total)
      chart.tooltip({
        showTitle: false
      })
      this.createLegend(chart, data)

      // 坐标系
      chart.coord('theta', {
        innerRadius: 0.85
      })
      chart
        .interval()
        .adjust('stack')
        .position('number')
        .color('alertEventName', [...COLORPICKER])
        .shape('slice-shape')
      chart.render()
      this.chart = chart
      this.listenChangeLegend()
    },

    createLegend(chart, data) {
      chart.legend(false)
      chart.legend({
        position: 'right-center',
        offsetX: 20,
        textStyle: {
          textAlign: 'center', // 文本对齐方向
          fill: '#ffffff', // 文本的颜色
          fontWeight: 'bold', // 文本粗细
          textBaseline: 'top' // 文本基准线
        },
        useHtml: true,
        containerTpl:
          '<div class="g2-legend g2-legend-custom" style="maxWidth:300px">' +
          '<h4 class="g2-legend-title"></h4>' +
          '<ul class="g2-legend-list J-g2-legend-list" style="list-style-type:none;margin:0;padding:0;"></ul>' +
          '</div>',
        itemTpl(value, color, checked, index) {
          const number = data[index].number
          return `<li class="g2-legend-list-item J-g2-legend-list-item item-{index} {checked}" data-color="{originColor}" data-value="{originValue}">
        <span>
        <i class="g2-legend-marker" style="background-color:{color};"></i>
        <span class="g2-legend-text">{value}</span></span><span class="g2-legend-number-text">${number}</span></li>`
        }
      })
    },

    /**
     * @Author: maogangfeng
     * @Date: 2020-03-20 14:41:34
     * @Desc: 设置图表辅助元素
     */
    setChartGuide(chart, total) {
      chart.guide().clear()
      chart.guide().html({
        position: ['50%', '50%'],
        html: `<div class="monitor-rate-guide"><label>${total}</label><span>事件总数</span></div>`,
        alignX: 'middle',
        alignY: 'middle'
      })
    },

    /**
     * @author xuchen
     * @date 2020-04-26 19:46:58
     * @desc 修改环形图大小
     */
    changeChartSize() {
      if (this.chart) {
        const { width, height } = this.chartSize[this.sizeMode]
        this.chart.changeSize(width, height)
      }
    },

    /**
     * @author xuchen
     * @date 2020-05-08 10:55:24
     * @desc 更新总数
     */
    refreshCountGuide(checked, value) {
      const { eventCount } = this
      const matchedItem = this.data.find(i => i.alertEventName === value)
      const { number = 0 } = matchedItem || {}
      this.eventCount = checked ? eventCount + number : eventCount - number
      this.setChartGuide(this.chart, this.eventCount)
    },

    /**
     * @author xuchen
     * @date 2020-04-24 14:37:10
     * @desc 监听图例的选择
     */
    listenChangeLegend() {
      this.$nextTick(() => {
        const liEleLen = document.querySelectorAll('.J-g2-legend-list .J-g2-legend-list-item')
          .length
        this.legendItemEvent.forEach(i => {
          i.removeEventListener('click', this.handleClickLengendItem)
        })
        this.legendItemEvent = []
        // 遍历所有的图例元素，绑定处理事件
        for (let i = 0; i < liEleLen; i++) {
          this.legendItemEvent[i] = document.querySelectorAll(
            '.J-g2-legend-list .J-g2-legend-list-item'
          )[i]
          this.legendItemEvent[i].addEventListener('click', this.handleClickLengendItem)
        }
      })
    },

    /**
     * @author xuchen
     * @date 2020-03-30 17:53:45
     * @desc 点击图例
     */
    handleClickLengendItem(e) {
      const className = e.currentTarget.getAttribute('class')
      const isChecked = className.indexOf('unChecked') !== -1
      const value = e.currentTarget.getAttribute('data-value')
      this.refreshCountGuide(isChecked, value)
    }
  }
}
</script>
<style lang="scss" scoped>
$typeStatistics-img-path: '~@/assets/images/OperationSpecialist/';
.chart {
  width: 452px;
  height: 452px;
  padding: 40px;
  background: url($typeStatistics-img-path+'chart_bg.png') no-repeat;
  background-size: 100% 100%;
}
::v-deep .g2-legend-custom {
  max-width: 350px !important;
  width: 300px !important;
  .g2-legend-list-item {
    display: flex !important;
    justify-content: space-between;
    height: 26px;
    .g2-legend-marker {
      width: 3px !important;
      height: 20px !important;
      border-radius: 0 !important;
      margin: 1px 8px 0 1px !important;
      vertical-align: middle;
    }
    .g2-legend-text {
      vertical-align: middle;
    }
    .g2-legend-number-text {
      width: 40px;
      padding-right: 6px;
      text-align: right;
      font-size: 14px;
      color: #5bf0fe;
      // text-align: right;
      line-height: 28px;
    }
  }
  .g2-legend-list-item.checked {
    background: url($typeStatistics-img-path+'legend_choose.png') no-repeat;
  }
  .g2-legend-list-item.unChecked {
    background: url($typeStatistics-img-path+'legend_not_choose.png') no-repeat;
  }
}
::v-deep .monitor-rate-guide {
  label,
  span {
    display: inline-block;
    width: 100%;
    text-align: center;
  }
  label {
    font-size: 40px;
    color: #ffffff;
  }
  span {
    font-size: 24px;
    color: #8feeff;
  }
}
</style>

<style lang="scss" scoped>
@media screen and (min-width: 1900px) {
  .chart {
    width: 431px;
    height: 427px;
    padding: 40px;
  }
  ::v-deep .monitor-rate-guide {
    margin-top: -10px;
  }
}

@media screen and (min-width: 1600px) and (max-width: 1899px) {
  .chart {
    width: 374px;
    height: 374px;
    padding: 31px;
  }
  ::v-deep .monitor-rate-guide {
    margin-top: -8px;
    label {
      font-size: 32px;
    }
    span {
      font-size: 20px;
    }
  }
  ::v-deep .g2-legend-text {
    font-size: 12px;
  }
  ::v-deep .g2-legend-number-text {
    font-size: 14px;
  }
  ::v-deep .g2-legend-custom {
    width: 226px !important;
    margin-left: 68px !important;
  }
}

@media screen and (max-width: 1599px) {
  .chart {
    width: 300px;
    height: 300px;
    padding: 25px;
  }
  ::v-deep .monitor-rate-guide {
    margin-top: -8px;
    label {
      font-size: 26px;
    }
    span {
      font-size: 16px;
    }
  }
  ::v-deep .g2-legend-list-item {
    margin-right: 0 !important;
  }
  ::v-deep .g2-legend-text {
    font-size: 10px;
  }
  ::v-deep .g2-legend-number-text {
    font-size: 12px;
  }
  ::v-deep .g2-legend-custom {
    width: 219px !important;
  }
}
</style>
